import { logger } from './loggingService';

export type SyncStatus = 'synced' | 'syncing' | 'offline' | 'error' | 'pending';

interface SyncState {
  status: SyncStatus;
  lastSyncTime: number | null;
  pendingChanges: number;
  lastError: string | null;
  isOnline: boolean;
}

type SyncListener = (state: SyncState) => void;

class SyncStatusService {
  private state: SyncState = {
    status: 'synced',
    lastSyncTime: null,
    pendingChanges: 0,
    lastError: null,
    isOnline: navigator.onLine
  };

  private listeners: Set<SyncListener> = new Set();
  private pendingQueue: Map<string, () => Promise<void>> = new Map();
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    window.addEventListener('online', () => this.handleOnlineChange(true));
    window.addEventListener('offline', () => this.handleOnlineChange(false));

    const saved = localStorage.getItem('cc_sync_state');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.state.lastSyncTime = parsed.lastSyncTime || null;
        this.state.pendingChanges = parsed.pendingChanges || 0;
      } catch { }
    }
  }

  private handleOnlineChange(isOnline: boolean) {
    logger.info('Sync', 'Network Status Change', { isOnline, previousStatus: this.state.status });
    this.state.isOnline = isOnline;

    if (isOnline) {
      if (this.state.pendingChanges > 0 || this.state.status === 'offline') {
        this.state.status = 'pending';
        this.processQueue();
      } else {
        this.state.status = 'synced';
      }
    } else {
      this.state.status = 'offline';
    }

    this.notify();
  }

  getState(): SyncState {
    return { ...this.state };
  }

  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify() {
    this.persistState();
    this.listeners.forEach(l => l(this.getState()));
  }

  private persistState() {
    localStorage.setItem('cc_sync_state', JSON.stringify({
      lastSyncTime: this.state.lastSyncTime,
      pendingChanges: this.state.pendingChanges
    }));
  }

  startSync() {
    if (this.state.status !== 'offline') {
      this.state.status = 'syncing';
      this.notify();
    }
  }

  syncSuccess() {
    this.state.status = 'synced';
    this.state.lastSyncTime = Date.now();
    this.state.lastError = null;
    this.state.pendingChanges = 0;
    this.notify();
    this.notify();
    logger.info('Sync', 'Sync Completed', { lastSyncTime: this.state.lastSyncTime });
  }

  syncFailed(error: string) {
    logger.warn('Sync', 'Sync Failed', { error, isOnline: this.state.isOnline });
    this.state.lastError = error;

    if (!this.state.isOnline || error.includes('network') || error.includes('fetch')) {
      this.state.status = 'offline';
    } else if (error.includes('401') || error.includes('unauthorized') || error.includes('token')) {
      this.state.status = 'error';
    } else {
      this.state.status = 'error';
    }

    this.state.pendingChanges++;
    this.notify();

    if (this.state.isOnline && !this.retryTimeout) {
      this.retryTimeout = setTimeout(() => {
        this.retryTimeout = null;
        this.processQueue();
      }, 30000);
    }
  }

  queueSync(key: string, syncFn: () => Promise<void>) {
    this.pendingQueue.set(key, syncFn);
    if (this.state.isOnline && this.state.status !== 'syncing') {
      this.processQueue();
    }
  }

  private async processQueue() {
    if (this.pendingQueue.size === 0) return;
    if (!this.state.isOnline) return;

    const entries = Array.from(this.pendingQueue.entries());
    this.pendingQueue.clear();

    this.state.status = 'syncing';
    this.notify();

    for (const [key, syncFn] of entries) {
      try {
        await syncFn();
      } catch (e) {
        logger.error('Sync', `Failed to process queued sync: ${key}`, e);
        this.pendingQueue.set(key, syncFn);
      }
    }

    if (this.pendingQueue.size === 0) {
      this.syncSuccess();
    } else {
      this.state.status = 'pending';
      this.notify();
    }
  }

  goOffline() {
    this.state.status = 'offline';
    this.notify();
  }

  clearPending() {
    this.state.pendingChanges = 0;
    this.pendingQueue.clear();
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    this.notify();
  }

  resetForAccountSwitch() {
    this.clearPending();
    this.state.status = 'synced';
    this.state.lastError = null;
    this.notify();
  }

  isOfflineMode(): boolean {
    return this.state.status === 'offline' || !this.state.isOnline;
  }
}

export const syncStatusService = new SyncStatusService();
